import type { RequestContext } from "../../../application/context/request-context.js";
import type { InspectionId, EquipmentId, ChecklistId } from "../../../shared/kernel/identifiers.js";
import type { Inspection } from "../../../domain/inspection/inspection-domain.js";
import type { Checklist, ChecklistComponent, ChecklistPhoto } from "../../../domain/inspection/checklist-domain.js";
import type { InspectionRepository, ChecklistRepository } from "../../../repositories/operational-repositories.js";
import { AppError, ErrorCode } from "../../../shared/errors/app-error.js";

export class InMemoryInspectionRepository implements InspectionRepository {
  private inspections = new Map<string, Inspection>();

  constructor() {
    try {
      const data = localStorage.getItem('inspect_inspections');
      if (data) this.inspections = new Map(JSON.parse(data));
    } catch (e) {
      console.warn("Failed to load inspections from localStorage", e);
    }
  }

  private persist() {
    try {
      localStorage.setItem('inspect_inspections', JSON.stringify(Array.from(this.inspections.entries())));
    } catch (e) {
      console.warn("Failed to save inspections to localStorage", e);
    }
  }

  private matchesClient(itemClientId: string, activeClientId?: string | null): boolean {
    if (!itemClientId || !activeClientId) return true;
    return itemClientId.toUpperCase() === activeClientId.toUpperCase();
  }

  async findById(context: RequestContext, id: InspectionId): Promise<Inspection | null> {
    const item = this.inspections.get(id);
    return (item && this.matchesClient(item.clientId, context.activeClientId)) ? item : null;
  }

  async findByEquipment(context: RequestContext, equipmentId: EquipmentId): Promise<readonly Inspection[]> {
    return Array.from(this.inspections.values()).filter(i => 
      this.matchesClient(i.clientId, context.activeClientId) && i.equipmentId === equipmentId
    );
  }

  async save(context: RequestContext, inspection: Inspection, expectedVersion?: number): Promise<void> {
    this.inspections.set(inspection.id, inspection);
    this.persist();
  }

  async listAll(context: RequestContext, options?: { projectId?: string | null }): Promise<Inspection[]> {
    return Array.from(this.inspections.values()).filter(i =>
      this.matchesClient(i.clientId, context.activeClientId) &&
      (!options?.projectId || i.projectId === options.projectId)
    );
  }

  async delete(context: RequestContext, id: InspectionId): Promise<void> {
    this.inspections.delete(id);
    this.persist();
  }
}

export class InMemoryChecklistRepository implements ChecklistRepository {
  private checklists = new Map<string, Checklist>();
  private components = new Map<string, ChecklistComponent>();
  private photos = new Map<string, ChecklistPhoto>();

  constructor() {
    try {
      const cl = localStorage.getItem('inspect_checklists');
      if (cl) this.checklists = new Map(JSON.parse(cl));
      const comp = localStorage.getItem('inspect_checklist_components');
      if (comp) this.components = new Map(JSON.parse(comp));
      const pht = localStorage.getItem('inspect_checklist_photos');
      if (pht) this.photos = new Map(JSON.parse(pht));
    } catch (e) {
      console.warn("Failed to load checklists from localStorage", e);
    }
  }

  private matchesClient(itemClientId: string, activeClientId?: string | null): boolean {
    if (!itemClientId || !activeClientId) return true;
    return itemClientId.toUpperCase() === activeClientId.toUpperCase();
  }

  private persist() {
    try {
      localStorage.setItem('inspect_checklists', JSON.stringify(Array.from(this.checklists.entries())));
      localStorage.setItem('inspect_checklist_components', JSON.stringify(Array.from(this.components.entries())));
      localStorage.setItem('inspect_checklist_photos', JSON.stringify(Array.from(this.photos.entries())));
    } catch (e) {
      console.warn("Failed to save checklists to localStorage", e);
    }
  }

  async findById(context: RequestContext, id: ChecklistId): Promise<Checklist | null> {
    const item = this.checklists.get(id);
    return (item && this.matchesClient(item.clientId, context.activeClientId)) ? item : null;
  }

  async findByInspection(context: RequestContext, inspectionId: InspectionId): Promise<Checklist | null> {
    return Array.from(this.checklists.values()).find(c => this.matchesClient(c.clientId, context.activeClientId) && c.inspectionId === inspectionId) || null;
  }

  async save(context: RequestContext, checklist: Checklist, expectedVersion?: number): Promise<void> {
    this.checklists.set(checklist.id, checklist);
    this.persist();
  }

  async delete(context: RequestContext, id: ChecklistId): Promise<void> {
    this.checklists.delete(id);
    for (const [compId, comp] of this.components.entries()) {
      if (comp.checklistId === id) {
        this.components.delete(compId);
        for (const [pId, photo] of this.photos.entries()) {
          if (photo.componentId === compId) {
            this.photos.delete(pId);
          }
        }
      }
    }
    this.persist();
  }

  async deleteByInspection(context: RequestContext, inspectionId: InspectionId): Promise<void> {
    for (const [id, chk] of this.checklists.entries()) {
      if (chk.inspectionId === inspectionId) {
        this.checklists.delete(id);
        for (const [compId, comp] of this.components.entries()) {
          if (comp.checklistId === chk.id) {
            this.components.delete(compId);
            for (const [pId, photo] of this.photos.entries()) {
              if (photo.componentId === compId) {
                this.photos.delete(pId);
              }
            }
          }
        }
      }
    }
    this.persist();
  }

  async saveComponent(context: RequestContext, component: ChecklistComponent): Promise<void> {
    // Same id-collision guard as SqliteChecklistRepository.saveComponent: an
    // id reused across two different checklists must never silently move
    // the row from the checklist that owns it onto the one being saved.
    const existing = this.components.get(component.id);
    if (existing && String(existing.checklistId) !== String(component.checklistId)) {
      throw new AppError(
        ErrorCode.Conflict,
        `CHECKLIST_COMPONENT_ID_COLLISION: component id ${component.id} already belongs to checklist ${existing.checklistId}, cannot reassign to ${component.checklistId}`,
        { safeMessage: 'Identidade de componente do Checklist em conflito com outra inspeção. A operação foi recusada para não sobrescrever histórico.' }
      );
    }
    this.components.set(component.id, component);
    this.persist();
  }

  async getComponents(context: RequestContext, checklistId: ChecklistId): Promise<readonly ChecklistComponent[]> {
    return Array.from(this.components.values()).filter(c => this.matchesClient(c.clientId, context.activeClientId) && c.checklistId === checklistId);
  }

  async deleteComponent(context: RequestContext, id: string): Promise<void> {
    this.components.delete(id);
    // Also delete associated photos
    for (const [pId, photo] of this.photos.entries()) {
      if (photo.componentId === id) {
        this.photos.delete(pId);
      }
    }
    this.persist();
  }

  async savePhoto(context: RequestContext, photo: ChecklistPhoto): Promise<void> {
    // Same id-collision guard as SqliteChecklistRepository.savePhoto.
    const existing = this.photos.get(photo.id);
    if (existing && String(existing.componentId) !== String(photo.componentId)) {
      throw new AppError(
        ErrorCode.Conflict,
        `CHECKLIST_PHOTO_ID_COLLISION: photo id ${photo.id} already belongs to component ${existing.componentId}, cannot reassign to ${photo.componentId}`,
        { safeMessage: 'Identidade de foto do Checklist em conflito com outro componente. A operação foi recusada para não sobrescrever histórico.' }
      );
    }
    this.photos.set(photo.id, photo);
    this.persist();
  }

  async getPhotos(context: RequestContext, componentId: string): Promise<readonly ChecklistPhoto[]> {
    return Array.from(this.photos.values()).filter(p => this.matchesClient(p.clientId, context.activeClientId) && p.componentId === componentId);
  }

  async clearPhotos(context: RequestContext, componentId: string): Promise<void> {
    for (const [id, photo] of this.photos.entries()) {
      if (photo.componentId === componentId && this.matchesClient(photo.clientId, context.activeClientId)) {
        this.photos.delete(id);
      }
    }
    this.persist();
  }
}
